import React from 'react';
import { motion } from 'framer-motion';
import { Shield, AlertTriangle, Eye, Brain, Network, Lock, BarChart2, Zap, CheckCircle, ArrowRight, Target, Database, Cloud } from 'lucide-react';

interface ThreatIntelligenceProps {
  setCurrentPage: (page: string) => void;
}

const ThreatIntelligence: React.FC<ThreatIntelligenceProps> = ({ setCurrentPage }) => {
  const features = [
    {
      icon: AlertTriangle,
      title: "Real-time Threat Detection",
      description: "Continuous monitoring and instant alerts for emerging threats targeting your infrastructure."
    },
    {
      icon: Eye,
      title: "Proactive Threat Hunting",
      description: "Advanced threat hunting capabilities to identify and eliminate potential security risks before they materialize."
    },
    {
      icon: Brain,
      title: "AI-Powered Analysis",
      description: "Machine learning algorithms that analyze patterns and predict potential security incidents."
    },
    {
      icon: Network,
      title: "Global Threat Network",
      description: "Access to worldwide threat intelligence feeds and industry-specific indicators of compromise."
    }
  ];

  const services = [
    {
      title: "Threat Detection & Analysis",
      items: [
        "Real-time threat monitoring",
        "Behavioral analysis",
        "Malware detection",
        "Vulnerability assessment"
      ]
    },
    {
      title: "Intelligence Gathering",
      items: [
        "Dark web monitoring",
        "Threat actor profiling",
        "Industry-specific intel",
        "Strategic intelligence"
      ]
    },
    {
      title: "Response & Mitigation",
      items: [
        "Incident response planning",
        "Threat containment",
        "Recovery procedures",
        "Post-incident analysis"
      ]
    }
  ];

  const capabilities = [
    {
      icon: Target,
      title: "Targeted Intelligence",
      description: "Industry-specific threat intelligence tailored to your organization's unique risk profile."
    },
    {
      icon: Database,
      title: "Intelligence Database",
      description: "Comprehensive database of known threats, vulnerabilities, and attack patterns."
    },
    {
      icon: Cloud,
      title: "Cloud Integration",
      description: "Seamless integration with cloud platforms for enhanced threat detection and response."
    }
  ];

  return (
    <div className="min-h-screen hero-gradient pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-block mb-6"
            >
              <Shield className="h-16 w-16 text-primary animate-glow" />
            </motion.div>
            
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-4xl md:text-5xl font-bold mb-6"
            >
              <span className="text-gradient">
              Threat Intelligence & Awareness
              </span>
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-lg text-muted max-w-3xl mx-auto"
            >
              Advanced threat detection and intelligence solutions to protect your organization from evolving cyber threats
            </motion.p>
          </div>
        </div>

        {/* Features Grid */}
        <div className="mb-20">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              <span className="text-gradient">Advanced Threat Intelligence Features</span>
            </h2>
            <p className="text-lg text-muted">
              Comprehensive threat intelligence and detection capabilities
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + index * 0.1 }}
                className="glass-panel rounded-xl p-6 hover-card"
              >
                <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg mb-6">
                  <feature.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-3">
                  {feature.title}
                </h3>
                <p className="text-muted">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Services Section */}
        <div className="mb-20">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              <span className="text-gradient">Intelligence Services</span>
            </h2>
            <p className="text-lg text-muted">
              Comprehensive threat intelligence and awareness services
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + index * 0.1 }}
                className="glass-panel rounded-xl p-8 hover-card"
              >
                <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">
                  {service.title}
                </h3>
                <ul className="space-y-4">
                  {service.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-primary mr-3" />
                      <span className="text-muted">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Advanced Capabilities */}
        <div className="mb-20">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              <span className="text-gradient">Advanced Capabilities</span>
            </h2>
            <p className="text-lg text-muted">
              State-of-the-art threat intelligence capabilities
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {capabilities.map((capability, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + index * 0.1 }}
                className="glass-panel rounded-xl p-8 text-center hover-card"
              >
                <div className="flex justify-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center">
                    <capability.icon className="h-8 w-8 text-white" />
                  </div>
                </div>
                <h3 className="text-2xl font-semibold mb-4">
                  {capability.title}
                </h3>
                <p className="text-muted">
                  {capability.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="text-center"
        >
          <div className="glass-panel rounded-2xl p-8 md:p-12">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl font-bold mb-6">
                Stay Ahead of Cyber Threats
              </h2>
              <p className="text-lg text-muted mb-8 max-w-2xl mx-auto">
                Protect your organization with advanced threat intelligence and awareness solutions
              </p>
              <button 
                onClick={() => setCurrentPage('get-started')}
                className="btn-primary"
              >
                <span className="flex items-center justify-center">
                  Request Consultation
                  <ArrowRight className="ml-2 h-5 w-5" />
                </span>
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ThreatIntelligence;